/**
 * pageTitleMap — central lookup of route paths → mobile-header
 * page titles.
 *
 * The desktop UI renders the page title via each screen's
 * `<PageHeader>` block, which is invisible on mobile because the
 * mobile content area scrolls below the sticky `<MobileHeader>`.
 * To surface the same title in the mobile header without forcing
 * every page to call a hook, we use this central map keyed by
 * route prefix.
 *
 * Lookup is longest-prefix wins so `/admin/messages` returns
 * `Podium` rather than `Command Center`.
 *
 * Spec: meridian-redesign — PR (c2)
 * Requirements: 14.9 (mobile header surfaces the active page
 *   title in place of the brand wordmark).
 */

export interface PageTitleEntry {
  /** Path or path prefix (longest match wins). */
  prefix: string;
  /** Title rendered in the mobile header. */
  title: string;
}

/**
 * Sorted longest-prefix-first so the lookup picks the most
 * specific match. New entries should be inserted into the right
 * position by length, or sorted at use site.
 */
export const PAGE_TITLE_MAP: ReadonlyArray<PageTitleEntry> = [
  // Spotlight (admin-only audience insights)
  { prefix: '/spotlight/program-impact', title: 'Program Impact' },
  { prefix: '/spotlight/social-metrics', title: 'Insight' },
  { prefix: '/spotlight', title: 'Spotlight' },

  // Admin product surfaces (longest first)
  { prefix: '/admin/participants', title: 'Contestants' },
  { prefix: '/admin/approvals', title: 'Applications' },
  { prefix: '/admin/calendar', title: 'Calendar' },
  { prefix: '/admin/documents', title: 'Documents' },
  { prefix: '/admin/messages', title: 'Podium' },
  { prefix: '/admin/settings', title: 'Settings' },
  { prefix: '/admin/users', title: 'Users' },
  { prefix: '/admin/merit', title: 'Merit' },
  { prefix: '/admin/envoy', title: 'Envoy' },
  { prefix: '/admin/patron', title: 'Patron' },
  { prefix: '/admin/vault', title: 'Vault' },
  { prefix: '/admin', title: 'Command Center' },

  // Portal product surfaces
  { prefix: '/portal/calendar', title: 'Calendar' },
  { prefix: '/portal/profile', title: 'My Profile' },
  { prefix: '/portal/documents', title: 'Documents' },
  { prefix: '/portal/service-log', title: 'Service Log' },
  { prefix: '/portal/messages', title: 'Podium' },
  { prefix: '/portal/insights', title: 'Spotlight' },
  { prefix: '/portal/settings', title: 'Settings' },
  { prefix: '/portal/merit', title: 'Merit' },
  { prefix: '/portal/envoy', title: 'Envoy' },
  { prefix: '/portal/vault', title: 'Vault' },
  { prefix: '/portal', title: 'Dashboard' },

  // Public + auth (nice-to-have; the hamburger header doesn't render
  // on these surfaces today, but listing them keeps the lookup honest).
  { prefix: '/about', title: 'About' },
  { prefix: '/contestants', title: 'Contestants' },
  { prefix: '/apply', title: 'Apply' },
  { prefix: '/signin', title: 'Sign In' },
  { prefix: '/signup', title: 'Sign Up' },
];

/**
 * Resolve the mobile-header title for a given pathname. Falls
 * back to `'Reignara'` if no entry matches (e.g. a transient
 * loading or 404 state).
 */
export function pageTitleForPath(pathname: string): string {
  // Iterate in declaration order — the array is hand-sorted so
  // longest prefixes come first. A simple `startsWith` match is
  // enough; we don't need a tree.
  for (const entry of PAGE_TITLE_MAP) {
    if (pathname === entry.prefix || pathname.startsWith(entry.prefix + '/')) {
      return entry.title;
    }
  }
  // Special case: bare `/` and unknown routes fall back to the
  // brand wordmark.
  return 'Reignara';
}
